<template>
  <div v-if="ingredients.length == 0 && !loading" class="no-results">
    <span>No results found</span>
  </div>
  <ListView
    class="ingredient-list"
    :data-items="ingredients"
    item="ingredientTile"
    footer="ingredientListFooter"
    @scroll="scrollHandler($event as unknown as Event)"
    data-cy="ingredient-list"
  >
    <template v-slot:ingredientTile="{ props }">
      <IngredientItemTile
        v-model="props.dataItem"
        :canModifyCarts="canModifyCarts"
      />
    </template>

    <template v-slot:ingredientListFooter>
      <Loader
        v-if="loading"
        class="ingredient-loader"
        size="large"
        type="pulsing"
        data-cy="ingredient-loader"
      />
    </template>
  </ListView>
</template>

<script setup lang="ts">
import { ref, watchEffect } from "vue";
import { ListView } from "@progress/kendo-vue-listview";
import { Loader } from "@progress/kendo-vue-indicators";
import IngredientItemTile from "./IngredientItemTile.vue";
import type { IngredientWithSelection } from "@/api/scheduleA";

const props = defineProps<{
  ingredients: IngredientWithSelection[];
  totalIngredients: number;
  defaultTake?: number;
  skip: number;
  loading: boolean;
  canModifyCarts: boolean;
}>();

const emit = defineEmits<{
  (e: "ingredientsChanged", skip: number, take: number): void;
}>();

const take = ref<number>(props.defaultTake ?? 10);
const skip = ref<number>(props.skip);

const loadScrollThreshold = 100;

const scrollHandler = function (e: Event) {
  const target = e.target as HTMLDivElement | null;
  if (!target) return;
  if (
    target.scrollTop + loadScrollThreshold <
    target.scrollHeight - target.clientHeight
  )
    return;
  if (skip.value + take.value >= props.totalIngredients) return;
  if (props.loading) return;

  skip.value = skip.value + take.value;
  emit("ingredientsChanged", skip.value, take.value);
};

watchEffect(() => {
  skip.value = props.skip;
});
</script>

<style lang="scss">
.ingredient-list {
  background: none !important;
  border: none !important;
  max-height: 70vh;
  margin: 16px 0 !important;

  .k-listview-content {
    padding: 15px 15px !important;
  }

  .k-listview-footer {
    border-top-width: 0 !important;
  }
}
.ingredient-loader {
  display: flex !important;
  justify-content: center;
  margin: 32px 0;
}

.no-results {
  display: flex;
  justify-content: center;
  margin: 32px 0;
  font-size: 1.5rem;
  font-weight: 500;
  color: #6c757d;
}
</style>
